import { ButtonLink, CurrencyInput, Divider, Input, Label, SubtractIcon } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';
import { useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '../../../Components/Shared/Dropdown';
import { convertToCents, convertToDollars } from '../../../helpers/money';
import { InvoicePreview } from '../widgets';

const OneTimeInvoicesForm = ({ control, watch, feeCategoryOptions, setValue, clearErrors, errors, register }) => {
  const { t } = useTranslation();

  const {
    fields: oneTimeFields,
    append: addOneTimeFee,
    remove: removeOneTimeFee,
  } = useFieldArray({
    control,
    name: 'oneTimeFees',
  });

  const [oneTimeFees, setOneTimeFees] = useState([
    { text: t('enrollments.enrollmentDeposit'), value: 'Enrollment/Registration deposit (refundable)' },
    { text: t('enrollments.enrollmentFee'), value: 'Enrollment/Registration fee (non-refundable)' },
    { text: t('billing.dropdown.MealFee'), value: 'Material Fee' },
    { text: t('billing.dropdown.ProcessingFee'), value: 'Processing fee' },
  ]);

  const oneTimeFeeTotal = watch('oneTimeFees')?.reduce((sum, fee) => sum + (convertToDollars(fee.amount) || 0), 0) || 0;
  const futureOneTimeFeeInvoices =
    watch('oneTimeFees')?.map((fee) => {
      return {
        label: fee.name,
        isProrated: false,
        amount: convertToDollars(fee.amount),
        dueDate: fee.dueDate,
      };
    }) || [];

  const invoiceDueString = (date: string | Date | Timestamp) => {
    if (typeof date === 'string') {
      return date;
    }
    if (date instanceof Timestamp) {
      return dayjs.unix(date.seconds).utc().format('YYYY-MM-DD');
    }
    return dayjs(date).format('YYYY-MM-DD');
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{t('enrollments.oneTimeEnrollmentFees')}</h2>
          <ButtonLink
            type="button"
            extraClasses="text-base"
            onClick={() => addOneTimeFee({ name: feeCategoryOptions.Fee[0].value, amount: 0, dueDate: new Date() })}
            content={t('enrollments.addOneTimeFee')}
          />
        </div>
        <div className="text-base">{t('enrollments.oneTimeEnrollmentFeesNote')}</div>
        {oneTimeFields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-6 gap-2">
            <div className="col-span-3 flex flex-col gap-3">
              <Label>{t('enrollments.feeType')}</Label>
              <Controller
                name={`oneTimeFees.${index}.name`}
                control={control}
                render={() => (
                  <Dropdown
                    allowAdditions
                    clearable
                    search
                    placeholder={t('enrollments.fees.typePlaceholder')}
                    label={t('enrollments.feeType')}
                    options={oneTimeFees}
                    required
                    onChange={(_e, data) => {
                      setValue(`oneTimeFees.${index}.name`, data.value);
                      clearErrors(`oneTimeFees.${index}.name`);
                    }}
                    onAddItem={(_e, data) => {
                      const newOption = {
                        text: data.value,
                        label: data.value,
                        value: data.value,
                      };
                      setOneTimeFees([...oneTimeFees, newOption]);
                      setValue(`oneTimeFees.${index}.name`, data.value);
                      clearErrors(`oneTimeFees.${index}.name`);
                    }}
                    value={watch(`oneTimeFees.${index}.name`)}
                    translator={t}
                    isForm={false}
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Controller
                name={`oneTimeFees.${index}.amount`}
                control={control}
                render={() => (
                  <CurrencyInput
                    label={t('enrollments.amount')}
                    onChange={({ target }) => {
                      const value = target.value.replace(',', '');
                      setValue(`oneTimeFees.${index}.amount`, convertToCents(parseFloat(value)));
                      clearErrors(`oneTimeFees.${index}.amount`);
                    }}
                    value={(() => {
                      const amount = watch(`oneTimeFees.${index}.amount`);
                      return amount && typeof amount === 'number' && amount > 0
                        ? convertToDollars(amount).toString()
                        : '';
                    })()}
                    required
                  />
                )}
              />
              {errors.oneTimeFees?.[index]?.amount && (
                <p className="text-sm text-red-950">{errors.oneTimeFees[index]?.amount?.message}</p>
              )}
            </div>
            <div>
              <Input
                type="date"
                inputType="date"
                {...register(`oneTimeFees.${index}.dueDate`)}
                label={t('enrollments.dueDate')}
                className="h-9 w-full rounded-md border p-2"
                value={invoiceDueString(watch(`oneTimeFees.${index}.dueDate`)).toString()}
                min={dayjs().format('YYYY-MM-DD')}
                max={dayjs().add(2, 'year').format('YYYY-MM-DD')}
              />
              <div className="text-xs">{t('enrollments.dueDateNote')}</div>
            </div>
            <div className="flex h-16 items-end pb-1">
              <SubtractIcon onClick={() => removeOneTimeFee(index)} className="size-6 cursor-pointer text-blue-900" />
            </div>
          </div>
        ))}
      </div>

      {oneTimeFeeTotal > 0 && (
        <InvoicePreview
          header={<>{t('enrollments.oneTimeInvoicePreviewTitle')}</>}
          nextInvoices={futureOneTimeFeeInvoices}
        />
      )}

      <Divider />
    </>
  );
};

export default OneTimeInvoicesForm;
